import {
  EXTERNAL_PROJECT_TYPES,
  type PeopleData,
  type PeopleIssue,
  type PeopleRule,
  type PeopleSheet,
} from '@lg/shared';

/**
 * Everything P9–P11 need from outside the file, fetched up front so the rules stay pure.
 * Groups come from the template company (the copy brings them into the venue); null when
 * they could not be read, in which case group checks are skipped with one warning.
 */
export interface MasterDataContext {
  templateCompanyId: string;
  groups: {
    customerGroups: ReadonlySet<string>;
    vendorGroups: ReadonlySet<string>;
    projectGroups: ReadonlySet<string>;
  } | null;
  /** Accounts the template copy brings along */
  templateCustomers: ReadonlySet<string>;
  templateVendors: ReadonlySet<string>;
  /** Workers already in the venue company (existing-venue imports) */
  existingPersonnelNumbers: ReadonlySet<string>;
}

/**
 * Rules for the master data tabs (customers, vendors, project contracts, projects):
 * P9 project managers and controllers, P10 customers and vendors, P11 projects and contracts.
 */
export function validateMasterData(data: PeopleData, ctx: MasterDataContext): PeopleIssue[] {
  const issues: PeopleIssue[] = [];
  const add = (
    rule: PeopleRule,
    sheet: PeopleSheet | null,
    rowNumber: number | null,
    column: string | null,
    message: string,
    severity: 'error' | 'warning' = 'error',
  ): void => {
    issues.push({ rule, sheet, rowNumber, column, severity, message });
  };
  const tpl = ctx.templateCompanyId;

  // P9 — project managers and controllers are workers, and managers hold the project manager role
  const workers = new Map(data.workers.map((w) => [w.personnelNumber, w]));
  const isWorker = (n: string): boolean => workers.has(n) || ctx.existingPersonnelNumbers.has(n);
  const projectManagers = new Set(
    data.roles.filter((r) => r.roleId === 'PROJPROJECTMANAGER').map((r) => r.upn),
  );
  for (const p of data.projects) {
    for (const [column, label, n] of [
      ['managerPersonnelNumber', 'Project manager', p.managerPersonnelNumber],
      ['controllerPersonnelNumber', 'Project controller', p.controllerPersonnelNumber],
    ] as const) {
      if (n && !isWorker(n))
        add('P9', 'projects', p.rowNumber, column, `${label} ${n} is not on the workers sheet`);
    }
    const manager = p.managerPersonnelNumber ? workers.get(p.managerPersonnelNumber) : undefined;
    if (manager && !projectManagers.has(manager.upn)) {
      add(
        'P9',
        'projects',
        p.rowNumber,
        'managerPersonnelNumber',
        `${manager.firstName} ${manager.lastName} manages ${p.projectId} but has no PROJPROJECTMANAGER role`,
        'warning',
      );
    }
  }

  // P10 — customers and vendors
  if (ctx.groups === null && (data.customers.length || data.vendors.length || data.projects.length)) {
    add(
      'P10',
      null,
      null,
      null,
      `Could not read the groups of ${tpl}; customer, vendor and project groups were not checked`,
      'warning',
    );
  }
  for (const c of data.customers) {
    if (ctx.groups && !ctx.groups.customerGroups.has(c.customerGroup))
      add(
        'P10',
        'customers',
        c.rowNumber,
        'customerGroup',
        `Customer group ${c.customerGroup} does not exist in ${tpl}`,
      );
    if (ctx.templateCustomers.has(c.customerAccount))
      add(
        'P10',
        'customers',
        c.rowNumber,
        'customerAccount',
        `${c.customerAccount} is already copied from ${tpl}; this row will be skipped`,
        'warning',
      );
  }
  for (const v of data.vendors) {
    if (ctx.groups && !ctx.groups.vendorGroups.has(v.vendorGroup))
      add(
        'P10',
        'vendors',
        v.rowNumber,
        'vendorGroup',
        `Vendor group ${v.vendorGroup} does not exist in ${tpl}`,
      );
    if (ctx.templateVendors.has(v.vendorAccount))
      add(
        'P10',
        'vendors',
        v.rowNumber,
        'vendorAccount',
        `${v.vendorAccount} is already copied from ${tpl}; this row will be skipped`,
        'warning',
      );
  }

  // P11 — projects and contracts
  const customers = new Set([...data.customers.map((c) => c.customerAccount), ...ctx.templateCustomers]);
  const contracts = new Set(data.projectContracts.map((c) => c.contractId));
  const usedContracts = new Set(data.projects.flatMap((p) => (p.contractId ? [p.contractId] : [])));
  for (const c of data.projectContracts) {
    if (!customers.has(c.customerAccount))
      add(
        'P11',
        'projectContracts',
        c.rowNumber,
        'customerAccount',
        `Customer ${c.customerAccount} is not on the customers sheet or in ${tpl}`,
      );
    if (!usedContracts.has(c.contractId))
      add(
        'P11',
        'projectContracts',
        c.rowNumber,
        'contractId',
        `No project uses contract ${c.contractId}`,
        'warning',
      );
  }

  const projects = new Map(data.projects.map((p) => [p.projectId, p]));
  for (const p of data.projects) {
    if (ctx.groups && !ctx.groups.projectGroups.has(p.projectGroup))
      add(
        'P11',
        'projects',
        p.rowNumber,
        'projectGroup',
        `Project group ${p.projectGroup} does not exist in ${tpl}`,
      );
    const external = EXTERNAL_PROJECT_TYPES.includes(p.projectType);
    if (external && !p.contractId)
      add('P11', 'projects', p.rowNumber, 'contractId', `${p.projectType} projects need a project contract`);
    if (p.projectType === 'Internal' && p.contractId)
      add('P11', 'projects', p.rowNumber, 'contractId', 'Internal projects have no project contract');
    if (p.contractId && !contracts.has(p.contractId))
      add(
        'P11',
        'projects',
        p.rowNumber,
        'contractId',
        `Contract ${p.contractId} is not on the project contracts sheet`,
      );
    if (p.endDate && p.endDate < p.startDate)
      add('P11', 'projects', p.rowNumber, 'endDate', 'The end date is before the start date');
    if (!p.parentProjectId) continue;
    const parent = projects.get(p.parentProjectId);
    if (p.parentProjectId === p.projectId) {
      add('P11', 'projects', p.rowNumber, 'parentProjectId', 'A project cannot be its own parent');
    } else if (!parent) {
      add(
        'P11',
        'projects',
        p.rowNumber,
        'parentProjectId',
        `Parent project ${p.parentProjectId} is not on the projects sheet`,
      );
    } else if (
      p.startDate < parent.startDate ||
      (parent.endDate && (p.endDate ?? p.startDate) > parent.endDate)
    ) {
      add(
        'P11',
        'projects',
        p.rowNumber,
        'startDate',
        `Dates fall outside parent project ${parent.projectId} (${parent.startDate} to ${parent.endDate ?? 'open'})`,
        'warning',
      );
    }
  }
  for (const id of projectCycle(data.projects)) {
    const p = projects.get(id);
    if (p) add('P11', 'projects', p.rowNumber, 'parentProjectId', `Parent projects loop back to ${id}`);
  }
  return issues;
}

/** Project IDs on a parent loop (a → b → a). */
function projectCycle(projects: PeopleData['projects']): Set<string> {
  const parentOf = new Map(
    projects.flatMap((p) => (p.parentProjectId ? [[p.projectId, p.parentProjectId] as const] : [])),
  );
  const inCycle = new Set<string>();
  for (const start of parentOf.keys()) {
    const path: string[] = [];
    let cur: string | undefined = start;
    while (cur && !path.includes(cur)) {
      path.push(cur);
      cur = parentOf.get(cur);
    }
    if (cur && cur !== parentOf.get(cur)) for (const id of path.slice(path.indexOf(cur))) inCycle.add(id);
  }
  return inCycle;
}

/** Projects in creation order: every parent before its children (loops left in sheet order). */
export function projectsInCreationOrder<
  T extends { projectId: string; parentProjectId?: string | undefined },
>(projects: readonly T[]): T[] {
  const byId = new Map(projects.map((p) => [p.projectId, p]));
  const out: T[] = [];
  const placed = new Set<string>();
  const visit = (p: T, trail: Set<string>): void => {
    if (placed.has(p.projectId) || trail.has(p.projectId)) return;
    trail.add(p.projectId);
    const parent = p.parentProjectId ? byId.get(p.parentProjectId) : undefined;
    if (parent) visit(parent, trail);
    placed.add(p.projectId);
    out.push(p);
  };
  for (const p of projects) visit(p, new Set());
  return out;
}
